import { useEffect, useRef } from "react";
import { Boxes, Maximize2, Network, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Node {
  id: string;
  x: number;
  y: number;
  label: string;
  type: "agent" | "tool" | "memory" | "human";
  vx?: number;
  vy?: number;
}

const NODES: Node[] = [
  { id: "a1", x: 0.5, y: 0.4, label: "Orchestrator", type: "agent" },
  { id: "a2", x: 0.25, y: 0.25, label: "Scout", type: "agent" },
  { id: "a3", x: 0.75, y: 0.25, label: "Synth", type: "agent" },
  { id: "a4", x: 0.25, y: 0.65, label: "Critic", type: "agent" },
  { id: "a5", x: 0.75, y: 0.65, label: "Scribe", type: "agent" },
  { id: "t1", x: 0.1, y: 0.45, label: "arxiv.search", type: "tool" },
  { id: "t2", x: 0.9, y: 0.45, label: "vector.embed", type: "tool" },
  { id: "m1", x: 0.5, y: 0.85, label: "Episodic Mem", type: "memory" },
  { id: "h1", x: 0.5, y: 0.12, label: "Human Gate", type: "human" },
];

const EDGES: [string, string][] = [
  ["a1", "a2"], ["a1", "a3"], ["a1", "a4"], ["a1", "a5"],
  ["a2", "t1"], ["a3", "t2"], ["a1", "h1"], ["a4", "m1"], ["a5", "m1"],
];

const TYPE_COLOR: Record<Node["type"], string> = {
  agent: "hsl(185 100% 55%)",
  tool: "hsl(270 90% 65%)",
  memory: "hsl(320 95% 60%)",
  human: "hsl(40 100% 60%)",
};

// Convert "hsl(H S% L%)" to "hsla(H, S%, L%, alpha)" — canvas requires comma syntax.
function withAlpha(hsl: string, alpha: number): string {
  const m = hsl.match(/hsl\(\s*([\d.]+)\s+([\d.]+%)\s+([\d.]+%)\s*\)/);
  if (!m) return hsl;
  return `hsla(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
}

export function AgentCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let raf = 0;
    let t = 0;
    const particles: { from: number; to: number; p: number; speed: number }[] = EDGES.map((_, i) => ({
      from: i,
      to: i,
      p: Math.random(),
      speed: 0.003 + Math.random() * 0.005,
    }));

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);
      t += 0.01;

      // Edges
      EDGES.forEach(([a, b], i) => {
        const na = NODES.find((n) => n.id === a)!;
        const nb = NODES.find((n) => n.id === b)!;
        const ax = na.x * w, ay = na.y * h;
        const bx = nb.x * w, by = nb.y * h;
        const grad = ctx.createLinearGradient(ax, ay, bx, by);
        grad.addColorStop(0, "hsla(185, 100%, 55%, 0.4)");
        grad.addColorStop(1, "hsla(270, 90%, 65%, 0.4)");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();

        // Flow particle
        const p = particles[i];
        p.p = (p.p + p.speed) % 1;
        const px = ax + (bx - ax) * p.p;
        const py = ay + (by - ay) * p.p;
        ctx.fillStyle = "hsla(185, 100%, 70%, 0.95)";
        ctx.shadowColor = "hsl(185 100% 55%)";
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Nodes
      NODES.forEach((n) => {
        const x = n.x * w, y = n.y * h;
        const color = TYPE_COLOR[n.type];
        const pulse = Math.sin(t * 2 + (n.x + n.y) * 5) * 0.3 + 0.7;

        // Glow
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 40);
        glow.addColorStop(0, withAlpha(color, 0.33));
        glow.addColorStop(1, withAlpha(color, 0));
        ctx.fillStyle = glow;
        ctx.fillRect(x - 40, y - 40, 80, 80);

        // Core
        ctx.fillStyle = "hsl(230 35% 6%)";
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, 14 * pulse + 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Inner dot
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();

        // Label
        ctx.fillStyle = "hsl(200 20% 95%)";
        ctx.font = '500 11px "JetBrains Mono", monospace';
        ctx.textAlign = "center";
        ctx.fillText(n.label, x, y + 28);
      });

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="relative h-full w-full bg-grid bg-grid-fade overflow-hidden">
      {/* Toolbar */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <div className="glass rounded-lg px-3 py-1.5 flex items-center gap-2">
          <Network className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-mono">9 nodes · 9 edges · live</span>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5"><Plus className="w-3 h-3" /> Add Node</Button>
        <Button size="sm" variant="outline" className="h-8 gap-1.5"><Boxes className="w-3 h-3" /> Auto-Layout</Button>
      </div>

      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <div className="glass rounded-lg p-2 flex flex-col gap-1.5 text-[10px] font-mono">
          {Object.entries(TYPE_COLOR).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: v, boxShadow: `0 0 6px ${v}` }} />
              <span className="capitalize text-muted-foreground">{k}</span>
            </div>
          ))}
        </div>
        <Button size="icon" variant="outline" className="h-8 w-8"><Maximize2 className="w-3 h-3" /></Button>
      </div>

      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Mini stats */}
      <div className="absolute bottom-4 left-4 z-10 glass rounded-lg p-3 flex gap-4">
        {[
          { l: "Throughput", v: "42.7K t/s", c: "text-primary" },
          { l: "Active", v: "4 agents", c: "text-secondary" },
          { l: "Bottleneck", v: "graph.compose", c: "text-warning" },
        ].map((s) => (
          <div key={s.l}>
            <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">{s.l}</div>
            <div className={`text-xs font-mono ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
